import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { useTriggerMessageFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/trigger-message.form-validation';
import { TriggerMessageModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/trigger-message.model';
import { PhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/charge-point/physical-socket.model';
import { MessageTrigger } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpp/src/enums/message-trigger.enum';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useChargePointContext } from '../../charge-point.context';

const ChargePointOcppTriggerMessageCreateFormStep1PhysicalSocketFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const chargePoint = useChargePointContext();
    const { control } =
      useFormContext<
        Pick<
          TriggerMessageModel,
          'chargePointId' | 'requestedMessage' | 'physicalSocketId'
        >
      >();
    const { physicalSocketId } = useTriggerMessageFormValidation();

    // DATA FETCHING ///////////////////////////////////////////////////////////

    const physicalSocketList = useMemo((): PhysicalSocketModel[] => {
      return chargePoint.channels.flatMap((channel) => channel.physicalSockets);
    }, [chargePoint.channels]);

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="physicalSocketId"
        control={control}
        rules={physicalSocketId}
        render={({ field, fieldState: { error } }) => {
          return (
            <Dropdown
              label={t(
                'delmonicos-shared-sdk.inputs-trigger-message-alt.physical-socket',
              )}
              placeholder={t(
                'delmonicos-shared-sdk.inputs-trigger-message-alt.physical-socket',
              )}
              removable
              required={!!physicalSocketId.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(value) => {
                field.onChange(value ?? '');
              }}>
              {physicalSocketList.map((physicalSocket) => (
                <Dropdown.Option
                  key={physicalSocket.id}
                  value={physicalSocket.id}>
                  {physicalSocket.ocppConnectorId}
                </Dropdown.Option>
              ))}
            </Dropdown>
          );
        }}
      />
    );
  };

const ChargePointOcppTriggerMessageCreateFormStep1RequestedMessageFragment =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { control } =
      useFormContext<
        Pick<
          TriggerMessageModel,
          'chargePointId' | 'requestedMessage' | 'physicalSocketId'
        >
      >();
    const { requestedMessage } = useTriggerMessageFormValidation();

    // DATA FETCHING ///////////////////////////////////////////////////////////

    const messageTriggerList = useMemo(() => Object.values(MessageTrigger), []);

    // RENDER //////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="requestedMessage"
        control={control}
        rules={requestedMessage}
        render={({ field, fieldState: { error } }) => {
          return (
            <Dropdown
              label={t(
                'delmonicos-shared-sdk.inputs-trigger-message.requested-message',
              )}
              placeholder={t(
                'delmonicos-shared-sdk.inputs-trigger-message.requested-message',
              )}
              removable
              required={!!requestedMessage.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(value) => {
                field.onChange(value ?? '');
              }}>
              {messageTriggerList.map((messageTrigger) => (
                <Dropdown.Option key={messageTrigger} value={messageTrigger}>
                  {messageTrigger}
                </Dropdown.Option>
              ))}
            </Dropdown>
          );
        }}
      />
    );
  };

export const ChargePointOcppTriggerMessageCreateFormStep1Controller =
  (): React.JSX.Element => {
    // RENDER ////////////////////////////////////////////////////////////////////

    return (
      <Stack
        direction="row"
        justifyContent="stretch"
        alignItems="stretch"
        flex={1}
        gap={GapEnum.XL}>
        <Stack
          direction="column"
          flex={1}
          alignItems="stretch"
          gap={GapEnum.XL}>
          <ChargePointOcppTriggerMessageCreateFormStep1PhysicalSocketFragment />
          <ChargePointOcppTriggerMessageCreateFormStep1RequestedMessageFragment />
        </Stack>
      </Stack>
    );
  };
